import { servicePost } from "./client.ts";
import { batchedServicePost } from "./readBatch.ts";
import type { ReturnObject } from "../types/returnObject.ts";
import type { TicketTech } from "./technician.ts";

/** StatusID for "Closed" in Web_Admin_Kiosk_Tech_Ticket_Status_Titles. Single
 *  source of truth — don't re-hardcode 4 at call sites. */
export const CLOSED_STATUS = 4;

export interface Ticket {
  ID: number;
  KioskID: number;
  IssueID: number;
  TechID: number;
  Status: number;
  Description: string;
  Solution: string;
  Created: string;
  LastUpdated: string;
  IsAuto: boolean;
  IPAddress: string;
  KioskRank: number;
}

export interface TicketHistory {
  ID: number;
  TicketID: number;
  Notes: string;
  Changed: string;
  AdminUserID: number;
  CreatorFullName: string;
  StatusID: number;
  AddedToSolution: boolean;
}

export interface TicketIssueType {
  ID: number;
  Issue: string;
  deleted: boolean;
  CategoryID: number | null;
  calcTS: number;
}

export interface TicketStatus {
  ID: number;
  Title: string;
  StatusID: number;
  calcTS: number;
}

export interface TicketIssueCategory {
  ID: number;
  Name: string;
  SortOrder: number;
  deleted: boolean;
  calcTS: number;
}

/** Get a single ticket by ID. */
export async function getTicket(ticketID: number): Promise<ReturnObject<Ticket>> {
  return batchedServicePost<Ticket>("WebGetTicket", { ticketID });
}

/** Get open ticket for a kiosk. */
export async function getOpenTicketForKiosk(kioskID: number): Promise<ReturnObject<Ticket>> {
  return servicePost<Ticket>("WebGetOpenTicketForKiosk", { kioskID });
}

/** Get all tickets for a kiosk (every status), most recently updated first. */
export async function getTicketsForKiosk(kioskID: number): Promise<ReturnObject<Ticket[]>> {
  return batchedServicePost<Ticket[]>("WebGetTicketsForKiosk", { kioskID });
}

/** Create a new ticket. Returns the ticket ID. */
export async function createTicket(
  kioskID: number,
  issueTypeID: number,
  issueDescription: string,
  techID: number,
  status: number,
  isTechnician: boolean,
): Promise<ReturnObject<number>> {
  return servicePost<number>("WebCreateTicket", { kioskID, issueTypeID, issueDescription, techID, status, isTechnician });
}

/** Update an existing ticket. */
export async function updateTicket(
  ticketID: number,
  issueDescription: string,
  issueSolution: string,
  status: number,
  techID: number,
  isTechnician: boolean,
  note: string,
  addTosolution: boolean,
): Promise<ReturnObject<boolean>> {
  return servicePost<boolean>("WebUpdateTicket", { ticketID, issueDescription, issueSolution, status, techID, isTechnician, note, addTosolution });
}

/** Get history events for a ticket. */
export async function getHistoryEvents(ticketID: number): Promise<ReturnObject<TicketHistory[]>> {
  return batchedServicePost<TicketHistory[]>("WebGetHistoryEvents", { ticketID });
}

/** Payload for adding a status/note event to a ticket. */
export interface NewTicketHistory {
  TicketID: number;
  StatusID: number;
  Notes: string;
  AdminUserID: number;
}

/**
 * Add a status/note history event to a ticket (also updates the ticket's status).
 * Does NOT touch technician assignment — use the Tickets page for reassignment.
 */

/** Email the assigned technician or merchandiser the standard ticket notification.
 *
 *  The mail body is email template type 38 and takes nothing from here but the ticket id —
 *  there is no way to say anything to the recipient through this call. `note` is recorded on
 *  the ticket's own history, appended to "… notified by e-mail", and is seen only by other
 *  operators. It used to take a `subject` too; that never reached anyone (MailMan reads
 *  `sRawSubject` only for raw and resend mails, never for a template row), so it is gone. */
export async function addHistoryEvent(history: NewTicketHistory, addToSolution: boolean): Promise<ReturnObject<boolean>> {
  return servicePost<boolean>("WebAddHistoryEvent", { history, addTosolution: addToSolution });
}

/** Notify technician by email. */
export async function notifyTech(
  ticketID: number,
  techID: number,
  isTechnician: boolean,
  note: string,
): Promise<ReturnObject<boolean>> {
  return servicePost<boolean>("WebTicketNotifyTech", { ticketID, techID, isTechnician, text: note });
}

/** Email a ticket-system summary report to management (server-side recipient list, no params). */
export async function sendTicketReport(): Promise<ReturnObject<boolean>> {
  return servicePost<boolean>("WebSendTicketReport", {});
}

/**
 * Escalate a ticket to Instant Systems support. The server sends it to Instant Desk when that is
 * configured (InstantDesk:BaseUrl), otherwise to Zendesk as before (WebEscalateTicketToSupport).
 */
export async function escalateTicketToSupport(
  ticketID: number,
  steps: TroubleshootingAnswer[],
  note: string,
): Promise<ReturnObject<boolean>> {
  return servicePost<boolean>("WebEscalateTicketToSupport", { ticketID, steps, note });
}

/** Escalate a ticket to Instant Systems support (support@instantsystems.se) — Zendesk turns the mail into a ticket. */
export async function escalateTicketToZendesk(
  ticketID: number,
  steps: TroubleshootingAnswer[],
  note: string,
): Promise<ReturnObject<boolean>> {
  return servicePost<boolean>("WebEscalateTicketToZendesk", { ticketID, steps, note });
}

/** One checklist step as answered before escalating. */
export interface TroubleshootingAnswer {
  Text: string;
  Tried: boolean;
}

/** Checklist steps for the ticket's issue category (category steps first, then general ones). */
export async function getTroubleshootingSteps(ticketID: number): Promise<ReturnObject<string[]>> {
  return servicePost<string[]>("WebGetTroubleshootingSteps", { ticketID });
}

/** Update/delete an issue type. */
export async function updateIssueType(issueID: number, issue: string, deleted: boolean, categoryID?: number | null): Promise<ReturnObject<boolean>> {
  return servicePost<boolean>("WebUpdateIssueType", { issueID, issue, deleted, categoryID: categoryID ?? null });
}

/** Create a custom issue type. */
export async function createCustomIssueType(issue: string, categoryID?: number | null): Promise<ReturnObject<number>> {
  return servicePost<number>("WebCreateCustomIssueType", { value: issue, categoryID: categoryID ?? null });
}

export { type TicketTech };
